
import { defineComponent, type PropType } from 'vue';
import { calculatePosition, MARGIN, type TooltipPosition, type TooltipAlign, type ArrowDirection } from './tooltip-positioning';

type TriggerMode = 'truncated' | 'always';

export default defineComponent({
  name: 'Tooltip',

  props: {
    content: {
      type: [String, Object],
      default: '',
    },
    // 'parent-control' for displaying the component
    elementAllowsTooltip: {
      type: Boolean,
      default: true,
    },
    triggerMode: {
      type: String as PropType<TriggerMode>,
      default: 'truncated',
    },
    hideArrow: {
      type: Boolean,
      default: false,
    },
    // Optional cap on the tooltip width in pixels. When provided, the tooltip
    // will use whichever is smaller: this value or the viewport-safe maximum.
    // Useful for shaping short tooltips without risking off-screen overflow.
    maxWidth: {
      type: Number,
      default: null,
    },
    // Controls which side of the trigger the tooltip appears on.
    // 'below' (default) — tooltip below, arrow points up toward the trigger.
    // 'above'           — tooltip above, arrow points down toward the trigger.
    // 'right'           — tooltip to the right, arrow points left.
    // 'left'            — tooltip to the left, arrow points right.
    position: {
      type: String as () => TooltipPosition,
      default: 'below',
      validator: (v: string) => ['below', 'above', 'left', 'right'].includes(v),
    },
    // When true, the tooltip will always use the requested `position` even if
    // there isn't enough room in the viewport. Useful when the caller has
    // deliberately placed the trigger and wants full control.
    disableFlip: {
      type: Boolean,
      default: false,
    },
    // Controls alignment along the cross-axis of the tooltip's side.
    // For 'below'/'above': horizontal alignment relative to the trigger.
    //   'start'  — left edge of tooltip aligns with left edge of trigger.
    //   'center' — tooltip is horizontally centered on the trigger.
    //   'end'    — right edge of tooltip aligns with right edge of trigger (default).
    // For 'left'/'right': vertical alignment relative to the trigger.
    //   'start'  — top edge of tooltip aligns with top edge of trigger.
    //   'center' — tooltip is vertically centered on the trigger (default).
    //   'end'    — bottom edge of tooltip aligns with bottom edge of trigger.
    // null (default) defers to the natural per-side default described above.
    align: {
      type: String as PropType<TooltipAlign | null>,
      default: null,
      validator: (v: unknown) => v === null || ['start', 'center', 'end'].includes(v as string),
    },
  },

  data() {
    return {
      showTooltip: false,
      tooltipStyle: {} as Record<string, string>,
      arrowStyle: {} as Record<string, string>,
      // Derived from `position` prop; kept in data so the template can read it
      // after it is calculated alongside tooltipStyle in allowTooltip().
      arrowDirection: 'up' as ArrowDirection,
    };
  },

  watch: {
    elementAllowsTooltip(): void {
      if (!this.elementAllowsTooltip) {
        this.showTooltip = this.elementAllowsTooltip;
      }
    },
    showTooltip(newVal: boolean): void {
      this.$emit('input', newVal);
      // On touch devices, `mouseout` never fires during a scroll gesture, so
      // the tooltip would stay visible. Attach a document-level touchmove
      // listener while visible and remove it as soon as the tooltip closes.
      if (newVal) {
        document.addEventListener('touchmove', this.disableTooltip, { passive: true });
      } else {
        document.removeEventListener('touchmove', this.disableTooltip);
      }
    },
  },

  beforeDestroy(): void {
    // Guard against a listener being left on the document if the component is
    // unmounted while the tooltip is still open (e.g. navigating away).
    document.removeEventListener('touchmove', this.disableTooltip);
  },

  methods: {
    allowTooltip(): void {
      const shouldShowByMode = this.triggerMode === 'always'
        ? true
        : this.textIsTruncated();

      if (shouldShowByMode && this.elementAllowsTooltip) {
        const rect = (this.$el as HTMLElement).getBoundingClientRect();

        // Cap max-width using the viewport-safe limit, optionally narrowed by prop.
        const viewportMaxWidth = rect.right - MARGIN;
        const maxWidth = this.maxWidth !== null
          ? Math.min(this.maxWidth, viewportMaxWidth)
          : viewportMaxWidth;

        const result = calculatePosition(rect, maxWidth, MARGIN, this.position, this.disableFlip, this.content, this.align);

        this.tooltipStyle = result.tooltipStyle;
        this.arrowStyle = result.arrowStyle;
        this.arrowDirection = result.arrowDirection;
        this.showTooltip = true;
      }
    },

    disableTooltip(): void {
      this.showTooltip = false;
    },

    textIsTruncated(): boolean {
      const defaultSlot = this.$slots?.default;
      const elm =
        defaultSlot && defaultSlot?.length > 0
          ? (defaultSlot[0].elm as HTMLElement)
          : null;
      if (elm) {
        return Math.ceil(elm.getBoundingClientRect().width) <= elm.scrollWidth;
      }
      return false;
    },
  },
});
